import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Group,
  Image,
  Loader,
  LoadingOverlay,
  Modal,
  Paper,
  Radio,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import React, { useEffect, useReducer, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  addIcon,
  closeIcon,
  editAction,
  locationIcon,
  noteIcon,
  removeIcon,
  stepBackColor,
  stepForwardColor,
} from '../../assets';
import FormErrorText from '../../components/FormErrorText';
import {
  cardItemDeleteService,
  cardItemUpdateService,
  cartNoteUpdateService,
  updateCartWithCheckout,
  useGetCartItems,
} from '../../services/cart-item/cart-item.service';
import { billSchema, couponSchema } from '../../utils/schema/formSchema';
import CheckoutButton from './CheckoutButton';
import { cartStateReducer, inititalTotalCartState } from './reducer';
import { useCartStyles, useCheckoutButtonStyles } from './style';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { customLoader } from '../../components/CustomLoader';
import { cartCheckout, updateNoteOrCouponCode } from '../../services/cart/cart.service';
import { useGlobalContext } from '../../context/global-context';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../config/RoutesName';
import { toastError, toastSuccess } from '../../config/Helper';
import { AxiosResponse, isAxiosError } from 'axios';
import { IBaseResponseError } from '../../typescript/interfaces/common.interface';
import { IGetCartItems, IVariant } from '../../typescript/interfaces/cart-item.interface';
import { useMutation } from '@tanstack/react-query';

const Cart = () => {
  const { classes, cx } = useCartStyles();
  const [opened, { open, close }] = useDisclosure(false);
  const navigate = useNavigate();
  const { classes: className } = useCheckoutButtonStyles();
  const { data, isError, error, isSuccess, isLoading, refetch, isFetching } = useGetCartItems();

  const [loading, setLoading] = useState(false);

  const [cartState, cartDispatch] = useReducer(cartStateReducer, inititalTotalCartState);

  const [coupnLoading, setCoupnLoading] = useState<boolean>(false);
  const [currVariantId, setCurrVariantId] = useState(-1);
  const [variants2, setVariants2] = useState<Record<string, IVariant[]>>({});
  const [totalAmount, seTtotalAmount] = useState({
    amountWithTax: 0,
    tax: 0,
  });
  // Inside your component:
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { cookies, setCookie, removeCookie } = useGlobalContext();

  const onSuccess = () => refetch();

  const _cardItemUpdateService = useMutation({
    mutationKey: ['card-item-update'],
    mutationFn: cardItemUpdateService,
    onSuccess,
  });

  const _cardItemDeleteService = useMutation({
    mutationKey: ['card-item-delete'],
    mutationFn: cardItemDeleteService,
    onSuccess,
  });

  useEffect(() => {
    if (isSuccess && !isFetching) {
      cartDispatch({
        type: 'INITIAL_CARTSTATE_CALCULATE',
        payload: {
          data: data?.data,
        },
      });

      const cartItemsRes = data?.data as IGetCartItems;
      let _variants: Record<string, IVariant[]> = {};

      cartItemsRes?.cart_items?.forEach((_item) => {
        let addonsTotal = 0;
        const addonLevelIds: IVariant['addon_level_ids'] = [];
        let addons: IVariant['addons'] = {};
        _item?.menu_item?.item_choices?.forEach((_item) => {
          _item?.addon_levels?.forEach((addonLevel) => {
            if (addonLevel?.is_in_cart) {
              addonsTotal += addonLevel?.amount;
              addonLevelIds.push(addonLevel?.addon_level_id);
              addons = {
                ...addons,
                [addonLevel?.addon_level_id]: {
                  addonId: addonLevel?.addon_level_id,
                  amount: addonLevel?.amount,
                  name: `${_item?.name} (${addonLevel?.name})`,
                },
              };
            }
          });
        });

        const currMenuItemId = String(_item?.menu_item?.menu_item_id) as keyof typeof _variants;
        const currMenuName = String(_item?.menu_item?.name);
        _variants = {
          ..._variants,
          [currMenuItemId]: [
            ...(_variants?.[currMenuItemId] ?? []),
            {
              menu_name: currMenuName,
              id: _item?.cart_item_id,
              quantity: _item?.quantity,
              addonsTotal,
              unitPrice: _item?.total_amount / _item?.quantity - addonsTotal,
              addon_level_ids: addonLevelIds,
              addons,
            },
          ],
        };
      });

      // _variants[1](v);

      setVariants2(_variants);
      seTtotalAmount({
        amountWithTax: cartItemsRes?.price?.amount_with_tax,
        tax: cartItemsRes?.tax_amount_exclusive,
      });
      setCookie('cart-item-count', data?.data?.items_count);
    }
    if (isError) {
      removeCookie('cart-item-count');
    }
  }, [isSuccess, isError, isFetching]);

  const {
    register: registerBill,
    handleSubmit: handleSubmitBill,
    resetField: resetBillField,
    setValue: setBillValue,
    control: controlBill,
    watch: watchBill,
    formState: { errors: errorsBill },
  } = useForm<z.infer<typeof billSchema>>({
    resolver: zodResolver(billSchema),
    mode: 'all',
    defaultValues: {
      contactType: 'email',
    },
  });

  const {
    register: registerCoupon,
    handleSubmit: handleSubmitCoupon,
    resetField: resetFieldCoupon,
    setValue: setValueCoupon,
    watch: watchCoupon,
    formState: { errors: errorsCoupon },
  } = useForm<z.infer<typeof couponSchema>>({
    resolver: zodResolver(couponSchema),
    mode: 'all',
  });

  const onSubmit: SubmitHandler<z.infer<typeof billSchema>> = async (data) => {
    setLoading(true);
    let apiResponse: any;
    try {
      const checkoutParams = {
        name: data.name,
        userEmail: data.userEmail,
        userPhone: data.userPhone,
        contactType: data.contactType === 'email' ? 'email' : 'phone',
        pay_with_cash: data?.pay_with_cash,
      };
      if (cartState.isCartModified) {
        const checkoutAfterUpdateCart: any = await updateCartWithCheckout({ cartData: cartState });
        if (checkoutAfterUpdateCart.success) {
          // toastSuccess(checkoutAfterUpdateCart.message);
          const responseData = await cartNoteUpdateService({ cartData: cartState });
          if (responseData.success) {
            apiResponse = await cartCheckout(checkoutParams);
          }
        }
      } else {
        apiResponse = await cartCheckout(checkoutParams);
      }
    } catch (error: unknown) {
      console.error(error);
      if (isAxiosError(error)) {
        const response = error.response as AxiosResponse<IBaseResponseError> | undefined;
        const data = response?.data;
        const hasApiError = data?.data?.error_type && data?.data?.error_message;
        // toastError(hasApiError ? data : data?.message);
      }
      setLoading(false);
    } finally {
      if (apiResponse?.success) {
        navigate(ROUTES.OTP, {
          state: {
            name: data.name,
            contactType: data.contactType,
            userEmail: data.userEmail,
            userPhone: data.userPhone,
            verifyToken: apiResponse.data.token,
          },
        });
        setLoading(false);
        removeCookie('cart-item-count');
        removeCookie('cart-item-price');
        removeCookie('current-restaurant-id');
      }
    }
  };

  const updateCoupon: SubmitHandler<z.infer<typeof couponSchema>> = async (data) => {
    setCoupnLoading(true);
    try {
      const resData: any = await updateNoteOrCouponCode({ type: 'coupon-code', couponCode: data.coupon_code });
      console.log(resData);

      cartDispatch({
        type: 'INITIAL_CARTSTATE_CALCULATE',
        payload: {
          data: resData?.data,
        },
      });
    } catch (error: unknown) {
      console.error(error);
    } finally {
      refetch();
      setCoupnLoading(false);
      close();
    }
  };

  // const updateNote = async (note: string) => {
  //   setLoading(true);
  //   try {
  //     await updateNoteOrCouponCode({ type: 'note', note: note });
  //   } catch (error: unknown) {
  //     console.error(error);
  //   } finally {
  //     setLoading(false);
  //     close();
  //   }
  // };

  const [noteModalType, setNoteModalType] = useState<'edit' | 'delete' | null>(null);

  const [noteText, setNoteText] = useState('');
  const [currentEditingNoteType, setCurrentEditingNoteType] = useState<'general-note' | 'menu-item-note' | null>(null);
  const [currentCartItemId, setCurrentCartItemId] = useState(-1);

  const checkoutHandler = () => {
    console.log('checkout');

    // navigate(ROUTES.OTP);
  };

  const incrementVariant = (menuItemId: string, cartItemId: number) => {
    cartItemId && setCurrVariantId(cartItemId);

    const currMenu = variants2?.[menuItemId]?.find((v) => v.id === cartItemId);
    let addonLevelIds: number[] = currMenu?.addon_level_ids ?? [];
    let quantity = (currMenu?.quantity ?? 0) + 1;
    let currItemPrice = currMenu?.unitPrice ?? 0;
    const addonTotal = currMenu?.addonsTotal ?? 0;

    setVariants2((prev) => ({
      ...prev,
      [menuItemId]: prev[menuItemId].map((v) => (v.id === cartItemId ? { ...v, quantity: v.quantity + 1 } : v)),
    }));

    _cardItemUpdateService.mutate(
      {
        addon_level_ids: addonLevelIds,
        quantity,
        _id: cartItemId,
      },
      {
        onSuccess: () => {
          const prevItemCount = +cookies['cart-item-count'];
          const previousCartItemPrice = +cookies['cart-item-price'];

          setCookie('cart-item-count', prevItemCount + 1);
          setCookie('cart-item-price', previousCartItemPrice + currItemPrice + addonTotal);
          setCurrVariantId(-1);
        },
      },
    );
  };

  const decrementVariant = (menuItemId: string, cartItemId: number) => {
    cartItemId && setCurrVariantId(cartItemId);
    const currMenu = variants2?.[menuItemId]?.find((v) => v.id === cartItemId);
    let addonLevelIds: number[] = currMenu?.addon_level_ids ?? [];
    let quantity = (currMenu?.quantity ?? 0) - 1;
    let currItemPrice = currMenu?.unitPrice ?? 0;
    const addonTotal = currMenu?.addonsTotal ?? 0;
    setVariants2((prev) => ({
      ...prev,
      [menuItemId]: prev[menuItemId]
        .map((v) => (v.id === cartItemId ? { ...v, quantity: v.quantity - 1 } : v))
        .filter((v) => v.quantity > 0),
    }));

    if (quantity) {
      _cardItemUpdateService.mutate(
        {
          addon_level_ids: addonLevelIds,
          quantity,
          _id: cartItemId,
        },
        {
          onSuccess: () => {
            const prevItemCount = +cookies['cart-item-count'];
            const previousCartItemPrice = +cookies['cart-item-price'];

            setCookie('cart-item-count', prevItemCount - 1);
            setCookie('cart-item-price', previousCartItemPrice - (currItemPrice + addonTotal));
            setCurrVariantId(-1);
          },
        },
      );
    } else {
      _cardItemDeleteService.mutate(
        {
          _id: cartItemId,
        },
        {
          onSuccess: () => {
            const prevItemCount = +cookies['cart-item-count'];
            const previousCartItemPrice = +cookies['cart-item-price'];

            setCookie('cart-item-count', prevItemCount - 1);
            setCookie('cart-item-price', previousCartItemPrice - (currItemPrice + addonTotal));
            setCurrVariantId(-1);
          },
        },
      );
    }
  };

  const deleteVariant = (menuItemId: string, cartItemId: number) => {
    const currMenu = variants2?.[menuItemId]?.find((v) => v.id === cartItemId);
    let quantity = currMenu?.quantity ?? 0;
    let currItemPrice = currMenu?.unitPrice ?? 0;
    const addonTotal = currMenu?.addonsTotal ?? 0;
    setVariants2((prev) => {
      const updated = prev[menuItemId].filter((v) => v.id !== cartItemId);

      if (!updated.length) {
        const { [menuItemId]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [menuItemId]: updated,
      };
    });

    _cardItemDeleteService.mutate(
      {
        _id: cartItemId,
      },
      {
        onSuccess: () => {
          const prevItemCount = +cookies['cart-item-count'];
          const previousCartItemPrice = +cookies['cart-item-price'];

          setCookie('cart-item-count', prevItemCount - quantity);
          setCookie('cart-item-price', previousCartItemPrice - (currItemPrice * quantity + addonTotal * quantity));
        },
      },
    );
  };

  return (
    <>
      <LoadingOverlay visible={isLoading || loading} pos='fixed' loader={customLoader} />
      {(!cartState.cartItems.length || !Object.entries(variants2)?.length) && !(isLoading || loading) && (
        <Box px={8}>
          <Text className={cx(classes.title)}>CART</Text>

          <Box mt={16}>
            <Text className={cx(classes.restaurantNameText)}>Your cart is empty</Text>
            <Box className={cx(classes.restaurantLocationFlexContainer)}>
              <Text className={cx(classes.restaurantAddressText)}>Add item into cart go to home page</Text>
            </Box>
          </Box>
        </Box>
      )}
      {(!!cartState.cartItems.length || !!Object.entries(variants2)?.length) && !(isLoading || loading) && (
        <Box px={8}>
          <Text className={cx(classes.title)}>CART</Text>

          <Box mt={16}>
            <Text className={cx(classes.restaurantNameText)}>{cartState?.associatedRestaurant?.name}</Text>
            <Box className={cx(classes.restaurantLocationFlexContainer)}>
              <Image src={locationIcon} width={16} height={16} />
              <Text className={cx(classes.restaurantAddressText)}>{cartState?.associatedRestaurant?.fullAddress}</Text>
            </Box>
          </Box>

          <Text c='magenta.9' fw={500} mt={16}>
            Main Item
          </Text>

          {Object.entries(variants2)?.map(([menuItemId, variants]) => (
            <Box key={menuItemId} pl={{ base: 0, sm: 4, md: 10 }}>
              <Text fw={600} c='#D61D4F'>
                {variants?.[0]?.menu_name}
              </Text>

              {isMobile ? (
                // ── MOBILE: Card stack layout ──────────────────────────────
                <Stack mt='sm' spacing='xs'>
                  {variants.map((v) => (
                    <Paper key={v.id} withBorder p='sm' radius='md'>
                      <Group position='apart' align='flex-start' noWrap>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size='sm' fw={500} style={{ wordBreak: 'break-word' }}>
                            {Object.values(v.addons)
                              .map((a) => a.name)
                              .join(' • ') || 'Default'}
                          </Text>
                          <Text size='xs' c='dimmed'>
                            Base ${v.unitPrice} • Addons ${v.addonsTotal.toFixed(2)}
                          </Text>
                        </Box>
                        <ActionIcon color='red' onClick={() => deleteVariant(menuItemId, v.id)}>
                          🗑
                        </ActionIcon>
                      </Group>

                      <Group position='apart' mt='xs' align='center'>
                        {/* Qty stepper */}
                        <Group spacing={4}>
                          <ActionIcon size='sm' onClick={() => incrementVariant(menuItemId, v.id)}>
                            +
                          </ActionIcon>
                          <Text fw={600} size='sm'>
                            {v.quantity}
                          </Text>
                          <ActionIcon size='sm' onClick={() => decrementVariant(menuItemId, v.id)}>
                            −
                          </ActionIcon>
                        </Group>

                        {/* Pricing */}
                        <Box ta='right'>
                          <Text size='xs' c='dimmed'>
                            Unit: ${(v.unitPrice + v.addonsTotal).toFixed(2)}
                          </Text>
                          <Text size='sm' fw={700}>
                            ${((v.unitPrice + v.addonsTotal) * v.quantity).toFixed(2)}
                          </Text>
                        </Box>
                      </Group>

                      {(_cardItemUpdateService?.isPending || _cardItemDeleteService?.isPending) &&
                        currVariantId === v.id && <Loader color='#D61D4F' size={16} mt={4} />}
                    </Paper>
                  ))}
                </Stack>
              ) : (
                // ── TABLET / DESKTOP: Table layout ────────────────────────
                <Table striped highlightOnHover mt='sm' style={{ tableLayout: 'fixed', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '55%' }}>Item</th>
                      <th style={{ width: '15%', textAlign: 'center' }}>Qty</th>
                      <th style={{ width: '12%', textAlign: 'right' }}>Unit</th>
                      <th style={{ width: '12%', textAlign: 'right' }}>Total</th>
                      <th style={{ width: '6%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => (
                      <tr key={v.id} style={{ backgroundColor: 'transparent', position: 'relative' }}>
                        <td>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size='sm' fw={500} style={{ wordBreak: 'break-word' }}>
                                {Object.values(v.addons)
                                  .map((a) => a.name)
                                  .join(' • ') || 'Default'}
                              </Text>
                              <Text size='xs' c='dimmed'>
                                Base ${v.unitPrice} • Addons ${v.addonsTotal.toFixed(2)}
                              </Text>
                            </Box>
                            {(_cardItemUpdateService?.isPending || _cardItemDeleteService?.isPending) &&
                              currVariantId === v.id && <Loader color='#D61D4F' size={20} />}
                          </Box>
                        </td>

                        <td align='center'>
                          <Group spacing={4} position='center' noWrap>
                            <ActionIcon size='sm' onClick={() => incrementVariant(menuItemId, v.id)}>
                              +
                            </ActionIcon>

                            <Text fw={600}>{v.quantity}</Text>
                            <ActionIcon size='sm' onClick={() => decrementVariant(menuItemId, v.id)}>
                              −
                            </ActionIcon>
                          </Group>
                        </td>

                        <td align='right'>${(v.unitPrice + v.addonsTotal).toFixed(2)}</td>
                        <td align='right'>${((v.unitPrice + v.addonsTotal) * v.quantity).toFixed(2)}</td>

                        <td align='right'>
                          <ActionIcon color='red' onClick={() => deleteVariant(menuItemId, v.id)}>
                            🗑
                          </ActionIcon>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Box>
          ))}

          {/* ---------------------------------- */}
          <Table>
            <tbody>
              <tr style={{ borderTop: '1px solid #7e7e7e' }}>
                <td colSpan={4}>
                  <Box className={classes.notesContainer}>
                    <Box style={{ flex: '0 0 auto', marginRight: 40 }}>
                      <Image src={noteIcon} width={14} height={17} />
                    </Box>

                    <Box style={{ flex: 1, overflowWrap: 'break-word' }}>
                      {/* <Text ta='center'>{cartState.note}</Text> */}
                      <Flex justify='center'>
                        <Text fw={500} fz={14}>
                          General Notes:
                        </Text>
                        <Text ta='center'>{cartState.note}</Text>
                      </Flex>
                    </Box>
                    <Flex align='center' style={{ flex: '0 0 auto', marginLeft: 40 }}>
                      <ActionIcon
                        onClick={() => {
                          open();
                          setNoteModalType('edit');
                          setCurrentEditingNoteType('general-note');
                          setNoteText(cartState.note);
                        }}>
                        <Image src={editAction} width={32} height={32} />
                      </ActionIcon>
                      <Text>|</Text>
                      <ActionIcon
                        onClick={() => {
                          open();
                          setNoteModalType('delete');
                        }}>
                        <Image src={closeIcon} width={32} height={32} />
                      </ActionIcon>
                    </Flex>
                  </Box>
                </td>
              </tr>
            </tbody>
          </Table>

          <Text fw={500} fz={16} c='red.6' mt={20} lh={1}>
            Coupon Code
          </Text>
          <Text c='red.6' lh={1} fz={14} mt={8}>
            Do you have any coupon code?
          </Text>
          <form onSubmit={handleSubmitCoupon(updateCoupon)}>
            <TextInput
              mt={8}
              placeholder='Type in ...'
              {...registerCoupon('coupon_code')}
              rightSection={
                <Box pr={64}>
                  <Button pos='absolute' top={12} right={10} type='submit' loading={coupnLoading}>
                    Apply
                  </Button>
                </Box>
              }
            />
            <FormErrorText field={errorsCoupon.coupon_code} />
          </form>

          <Box style={{ borderBlock: '1px solid #7e7e7e', marginTop: 16, paddingBlock: 8 }}>
            <Text fw={500} lh={1} c='red.6'>
              Bill Details
            </Text>
            <Flex direction='column' mt={16} gap={4}>
              <Flex justify='space-between'>
                <Text fz={14} fw={500}>
                  Food Items
                </Text>
                <Text fz={14} fw={500}>
                  {`$ ${
                    cartState?.price?.couponDiscount
                      ? (cartState.totalPrice + cartState?.price?.couponDiscount).toFixed(2)
                      : cartState.totalPrice.toFixed(2)
                  }`}
                </Text>
              </Flex>

              {/* <Flex justify='space-between'>
                <Text fz={14} fw={500}>
                  Restaurant Amount
                </Text>
                <Text fz={14} fw={500}>
                  $ 3.00
                </Text>
              </Flex> */}

              <Flex justify='space-between'>
                <Text fz={14} fw={500}>
                  Coupon Apply
                </Text>
                {cartState?.price?.couponDiscount ? (
                  <Text fz={14} fw={500}>
                    - $ {cartState?.price?.couponDiscount}
                  </Text>
                ) : (
                  <Text fz={14} fw={500}>
                    Not Applied
                  </Text>
                )}
              </Flex>

              {/* <Flex justify='space-between'>
                <Text fz={14} fw={500}>
                  Other Discount
                </Text>
                <Text fz={14} fw={500}>
                  -$ 3.00
                </Text>
              </Flex> */}
              <Flex justify='space-between'>
                <Text fz={14} fw={500}>
                  Tax
                </Text>
                <Text fz={14} fw={500}>
                  $ {(totalAmount?.tax / 100).toFixed(2)}
                </Text>
              </Flex>
              <Flex>
                <Text fz={14} fw={700} mr={4}>
                  Note:
                </Text>
                <Text fz={14} fw={400}>
                  Applicable taxes will be added to the final amount.
                </Text>
              </Flex>
            </Flex>
          </Box>

          <Box style={{ borderBottom: '1px solid #7e7e7e', paddingBlock: 8 }}>
            <Flex justify='space-between'>
              <Text fz={20} fw={500}>
                To Pay
              </Text>
              <Text fz={20} fw={500}>
                {/* {`$ ${(+cookies['cart-item-price'] + 8.0 - (5.0 + 3.0)).toFixed(2)}`} */}
                {`$ ${totalAmount?.amountWithTax.toFixed(2)}`}
              </Text>
            </Flex>
          </Box>

          <Box style={{ borderBottom: '1px solid #7e7e7e', paddingBlock: 16, marginBottom: 72 }}>
            <Text fz={16} lh={1} fw={500} c='red.6'>
              Contact Information
            </Text>

            <form onSubmit={handleSubmitBill(onSubmit)}>
              <Checkbox
                radius='xs'
                label='pay with cash'
                tt='capitalize'
                my={20}
                fw={500}
                checked={watchBill('pay_with_cash')}
                onChange={(event) =>
                  setBillValue('pay_with_cash', event.target.checked, {
                    shouldValidate: true,
                  })
                }
                styles={{
                  input: {
                    cursor: 'pointer',
                  },
                  label: {
                    cursor: 'pointer',
                  },
                }}
              />
              <TextInput placeholder='Your name' label='Name' withAsterisk {...registerBill('name')} />
              <FormErrorText field={errorsBill.name} />
              <Controller
                control={controlBill}
                name='contactType'
                render={({ field: { onChange, ...field } }) => (
                  <Radio.Group
                    {...field}
                    onChange={(e: any) => {
                      setBillValue('contactType', e);
                      resetBillField(e === 'email' ? 'userPhone' : 'userEmail');
                    }}>
                    <Flex justify='space-between' mt={16}>
                      <Radio value='email' label='Email' />
                      <Radio value='phone' label='Phone' />
                    </Flex>
                  </Radio.Group>
                )}></Controller>
              {watchBill('contactType') === 'email' && (
                <>
                  <TextInput
                    mt={16}
                    placeholder='Type in ...'
                    label='Email'
                    withAsterisk
                    {...registerBill('userEmail')}
                  />
                  <FormErrorText field={errorsBill.userEmail} />
                </>
              )}
              {watchBill('contactType') === 'phone' && (
                <>
                  <TextInput
                    mt={16}
                    placeholder='(000) 000 0000'
                    label='Phone No'
                    withAsterisk
                    {...registerBill('userPhone')}
                  />
                  <FormErrorText field={errorsBill.userPhone} />
                </>
              )}

              {/* <Button className={className.button} type='submit'>
                <Flex w='100%' justify='space-between'>
                  <Text lh={1}>Checkout</Text>
                  <Text lh={1}>|</Text>
                  <Text lh={1}>${cartState.totalPrice.toFixed(2)}</Text>
                </Flex>
              </Button> */}
              <CheckoutButton amount={totalAmount?.amountWithTax.toFixed(2)} onClick={checkoutHandler} />
            </form>

            <Text fz={12} fw={500} mt={16}>
              * By providing your phone number, you agree to receive one-time passwords(OTP), order status updates, and
              order receipt links through text messages from us. Messaging and data rates may apply. You may opt out at
              any time by replying "STOP". For more information, please visit to our privacy policy and terms and
              conditions.
            </Text>
          </Box>
        </Box>
      )}
      <Modal opened={opened} onClose={close} centered withCloseButton={false}>
        <Box>
          {noteModalType === 'edit' ? (
            <>
              <Flex justify='space-between'>
                <Text c='red.6' fw={500}>
                  Edit note
                </Text>
                <ActionIcon>
                  <Image
                    src={closeIcon}
                    onClick={() => {
                      close();
                      setNoteModalType(null);
                    }}
                  />
                </ActionIcon>
              </Flex>
              <Text fw={500} fz={14} mt={8}>
                Please leave a note for the chef
              </Text>
              <Textarea
                label='Message'
                autosize
                minRows={2}
                mt={16}
                value={noteText}
                onChange={(e) => {
                  setNoteText(e.target.value);
                }}
              />

              <Flex justify='center'>
                <Button
                  bg='red.6'
                  radius={4}
                  mt={16}
                  onClick={() => {
                    close();
                    if (currentEditingNoteType === 'general-note') {
                      cartDispatch({
                        type: 'NOTE_CHANGE',
                        payload: {
                          for: 'general-note',
                          note: noteText,
                        },
                      });
                    } else {
                      cartDispatch({
                        type: 'NOTE_CHANGE',
                        payload: {
                          for: 'menu-item-note',
                          note: noteText,
                          cartItemIndex: currentCartItemId,
                        },
                      });
                    }
                  }}>
                  <Text>Update</Text>
                </Button>
              </Flex>
            </>
          ) : null}

          {noteModalType === 'delete' ? (
            <>
              <Flex justify='space-between'>
                <Text c='red.6' fw={500}>
                  Delete
                </Text>
                <ActionIcon>
                  <Image
                    src={closeIcon}
                    onClick={() => {
                      close();
                      setNoteModalType(null);
                    }}
                  />
                </ActionIcon>
              </Flex>
              <Text fw={500} fz={14} mt={8} ta='center'>
                Are you sure you want to delete this note?
              </Text>
              <Flex mt={32} justify='space-between' gap={16}>
                <UnstyledButton className={cx(classes.modalButton, classes.noButton)} onClick={close}>
                  <Text>No</Text>
                </UnstyledButton>

                <UnstyledButton className={cx(classes.modalButton, classes.yesButton)} onClick={close}>
                  <Text>Yes</Text>
                </UnstyledButton>
              </Flex>
            </>
          ) : null}
        </Box>
      </Modal>
    </>
  );
};

export default Cart;
